/**
 * AlarmSlideOver — single-alarm detail slide-over panel for the customer
 * global Alarms hub at `/alarms` (EPIC #225 sub 7/10, spec §6.4).
 *
 * Triggered by row click on the global hub or by `?alarm=<site>:<device>:<cond>`
 * deep-link (e.g., from the bell popover in a future sibling sub-issue).
 * Right-anchored on desktop; full-screen modal on small breakpoints.
 *
 * Pane-of-glass v1: read-only. No ack / clear / resolve buttons (DP#2
 * read-only-v1). Edge transitions remain the single source of truth for
 * alarm state.
 *
 * Identity is the canonical (site_id, device_id, condition_key) tuple —
 * see `alarm-deep-link.ts` for URL encoding.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useAlarmDetail } from '../../hooks/useAlarmDetail';
import { useSites } from '../../hooks/useSites';
import { TransitionTimeline } from './TransitionTimeline';
import { SeverityPill } from './primitives/SeverityPill';
import { deriveAlarmStatus, type ActiveAlarm } from '../../types/alarm.types';
import { cn } from '../../utils/cn';
import type { AlarmIdentity } from './alarm-deep-link';

/** Spec §6.4: "last 20 lifecycle events for this alarm". */
const MAX_TRANSITIONS = 20;

interface Props {
  identity: AlarmIdentity | null;
  onClose: () => void;
}

export function AlarmSlideOver({ identity, onClose }: Props) {
  // Wire identity carries the site_code (e.g. 'HONDO-01') because that's what
  // alarms.active_alarms.site_id stores. The per-site detail route at
  // /api/v1/sites/:siteId/alarms/:deviceId/:conditionKey resolves siteId via
  // config.sites.site_id (a UUID), so we MUST bridge code → UUID before
  // calling useAlarmDetail. Mirrors useGlobalAlarmSubscription.ts:43-67.
  const sitesQ = useSites();
  const siteUuid = useMemo(() => {
    if (!identity) return '';
    const sites = sitesQ.data?.sites ?? [];
    return sites.find((s) => s.site_code === identity.siteId)?.site_id ?? '';
  }, [identity, sitesQ.data]);

  const detailQ = useAlarmDetail(
    siteUuid,
    identity?.deviceId ?? '',
    identity?.conditionKey ?? '',
  );
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Stable string key for identity so effects don't re-fire on every URL
  // change (filter-chip clicks rebuild the searchParams ref upstream and
  // would otherwise bounce focus on every render).
  const focusKey = identity
    ? `${identity.siteId}:${identity.deviceId}:${identity.conditionKey}`
    : null;

  // Esc closes the slide-over (spec §6.4).
  useEffect(() => {
    if (!focusKey) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusKey, onClose]);

  // Move focus into panel on open; restore previous focus on close. Full Tab
  // cycling (focus trap) is a v1.5 a11y polish — out of scope for v1.
  useEffect(() => {
    if (!focusKey) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      // isConnected catches the realistic failure mode (the previously-focused
      // node was unmounted while the slide-over was open).
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [focusKey]);

  if (!identity) return null;

  const alarm = detailQ.data?.active ?? null;
  const transitions = (detailQ.data?.transitions ?? []).slice(0, MAX_TRANSITIONS);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Alarm detail"
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="alarm-slide-over"
    >
      {/* Click-out backdrop — covers the area NOT occupied by the panel. */}
      <button
        type="button"
        aria-label="Close alarm detail"
        onClick={onClose}
        data-testid="alarm-slide-over-backdrop"
        className="absolute inset-0 bg-black/40 cursor-default"
        tabIndex={-1}
      />
      <aside
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          'relative h-full bg-ems-bg overflow-y-auto shadow-2xl border-l border-ems-border',
          'w-full sm:w-[480px] sm:max-w-[90vw]',
          'focus:outline-none',
        )}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-2 p-4 border-b border-ems-border bg-ems-bg">
          <div className="min-w-0 flex items-center gap-2">
            {alarm && <SeverityPill severity={alarm.severity} variant="pill" />}
            <h2 className="text-sm font-semibold text-ems-text-primary truncate">
              {alarm?.condition_label ?? identity.conditionKey}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="alarm-slide-over-close"
            className="text-ems-text-muted hover:text-ems-text-primary p-1 rounded focus:outline-none focus:ring-2 focus:ring-ems-blue/40"
          >
            ✕
          </button>
        </header>

        <div className="p-4 space-y-4">
          {detailQ.isLoading && (
            <p className="text-xs text-ems-text-muted py-4 text-center" data-testid="alarm-slide-over-loading">
              Loading alarm detail…
            </p>
          )}
          {detailQ.isError && (
            <p
              className="text-xs text-ems-red border border-ems-red/30 rounded p-3"
              data-testid="alarm-slide-over-error"
            >
              Could not load alarm detail.
            </p>
          )}
          {!detailQ.isLoading && !detailQ.isError && !alarm && (
            <p
              className="text-xs text-ems-text-muted border border-ems-border rounded p-3"
              data-testid="alarm-slide-over-not-found"
            >
              Alarm is no longer active. It may have been cleared at the site.
            </p>
          )}
          {alarm && (
            <>
              <AlarmMetadata alarm={alarm} />
              <section>
                <h3 className="text-[9px] uppercase tracking-wider text-ems-text-muted mb-2">
                  Transition history (last {transitions.length})
                </h3>
                <TransitionTimeline events={transitions} />
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function AlarmMetadata({ alarm }: { alarm: ActiveAlarm }) {
  const status = deriveAlarmStatus(alarm);
  return (
    <dl
      className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs"
      data-testid="alarm-slide-over-metadata"
    >
      <Row label="Site" value={alarm.site_id} />
      <Row label="Device" value={alarm.device_id} />
      <Row label="Block" value={alarm.block_id ?? '—'} />
      <Row label="Type" value={alarm.device_type} />
      <Row label="Condition key" value={alarm.condition_key} mono />
      <Row label="Category" value={alarm.category} />
      <Row label="Raised" value={new Date(alarm.raised_at).toLocaleString()} />
      <Row label="Status" value={status} />
      {alarm.acked_at && <Row label="Acked at" value={new Date(alarm.acked_at).toLocaleString()} />}
      {alarm.acked_by && <Row label="Acked by" value={alarm.acked_by} />}
      {alarm.cleared_at && (
        <Row label="Cleared" value={new Date(alarm.cleared_at).toLocaleString()} />
      )}
      {alarm.latching && <Row label="Latching" value="yes" />}
    </dl>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-ems-text-muted whitespace-nowrap">{label}</dt>
      <dd className={cn('text-ems-text-primary truncate', mono && 'font-mono text-[11px]')}>
        {value}
      </dd>
    </>
  );
}
